import api from './api';

export interface ActualDataSource {
  id: number;
  name: string;
  description?: string;
  jdbcUrl: string;
  username: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreditType {
  id: number;
  name: string;
}

export interface CashFlowData {
  cashFlow: { [month: string]: number };
  totalCases: number;
  startDate: string;
  endDate: string;
  purchasePrice: number;
  initialDebt: number;
  creditTypeNames?: string;
}

export interface CreditTypeSummary {
  creditTypeName: string;
  creditTypeId: number;
  totalCases: number;
  totalPurchasePrice: number;
  totalCurrentDebt: number;
  totalInitialDebt: number;
}

export interface MonthSummary {
  year: number;
  month: number;
  totalCases: number;
  creditTypes: { [key: string]: CreditTypeSummary };
}

export interface YearSummary {
  year: number;
  totalCases: number;
  months: { [key: number]: MonthSummary };
}

export interface ActualDataAggregation {
  years: { [key: number]: YearSummary };
}

export const actualDataService = {
  // Data sources
  createDataSource: async (data: {
    name: string;
    description?: string;
    jdbcUrl: string;
    username: string;
    password: string;
  }): Promise<ActualDataSource> => {
    const response = await api.post<ActualDataSource>('/actual-data/sources', data);
    return response.data;
  },

  getDataSources: async (): Promise<ActualDataSource[]> => {
    const response = await api.get<ActualDataSource[]>('/actual-data/sources');
    return response.data;
  },

  getDataSource: async (id: number): Promise<ActualDataSource> => {
    const response = await api.get<ActualDataSource>(`/actual-data/sources/${id}`);
    return response.data;
  },

  updateDataSource: async (id: number, data: Partial<ActualDataSource>): Promise<ActualDataSource> => {
    const response = await api.put<ActualDataSource>(`/actual-data/sources/${id}`, data);
    return response.data;
  },

  deleteDataSource: async (id: number): Promise<void> => {
    await api.delete(`/actual-data/sources/${id}`);
  },

  testConnection: async (id: number): Promise<{ message: string; valid: boolean }> => {
    const response = await api.post(`/actual-data/sources/${id}/test`);
    return response.data;
  },

  // Data queries
  getCreditTypes: async (id: number): Promise<CreditType[]> => {
    const response = await api.get<CreditType[]>(`/actual-data/sources/${id}/credit-types`);
    return response.data;
  },

  getCashFlow: async (
    id: number,
    yearMonth: string,
    creditTypeIds: number[]
  ): Promise<CashFlowData> => {
    const response = await api.post<CashFlowData>(`/actual-data/sources/${id}/cash-flow`, {
      yearMonth,
      creditTypeIds,
    });
    return response.data;
  },

  // OPTIMIZED: Lazy loading endpoints
  getYears: async (id: number): Promise<Array<{ year: number; totalCases: number }>> => {
    const response = await api.get<Array<{ year: number; totalCases: number }>>(
      `/actual-data/sources/${id}/years`
    );
    return response.data;
  },

  getMonths: async (id: number, year: number): Promise<Array<{ month: number; totalCases: number }>> => {
    const response = await api.get<Array<{ month: number; totalCases: number }>>(
      `/actual-data/sources/${id}/months/${year}`
    );
    return response.data;
  },

  getCreditTypesForMonth: async (
    id: number,
    year: number,
    month: number
  ): Promise<Array<CreditTypeSummary>> => {
    const response = await api.get<Array<CreditTypeSummary>>(
      `/actual-data/sources/${id}/credit-types/${year}/${month}`
    );
    return response.data;
  },

  // Cache management
  populateCache: async (id: number): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/actual-data/sources/${id}/populate-cache`);
    return response.data;
  },
  hasCache: async (id: number): Promise<{ hasCache: boolean }> => {
    const response = await api.get<{ hasCache: boolean }>(`/actual-data/sources/${id}/has-cache`);
    return response.data;
  },
};
